// Integration voting / waiting list (VERA-407) react-query hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { integrationRequestsApi } from 'services'
import type {
  IntegrationAnalytics,
  IntegrationVoter,
  RoadmapIntegration,
  RoadmapUpdate,
} from 'services/integrationRequestsApi'

const LIST_KEY = ['integration-requests']
const INTERNAL_KEY = ['internal', 'integration-requests']

export const useIntegrationRequests = () =>
  useQuery({
    queryKey: LIST_KEY,
    queryFn: () => integrationRequestsApi.list(),
    staleTime: 30000,
    retry: false,
  })

export const useToggleVote = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      integrationRequestsApi.toggleVote(requestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

export const useSuggestIntegration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name: string; category: string; description?: string; forceSubmit?: boolean }) =>
      integrationRequestsApi.suggest(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

// --- Internal Labs curation queue ---
export const useInternalIntegrationRequests = (status: 'pending_review' | 'all' = 'pending_review') =>
  useQuery({
    queryKey: [...INTERNAL_KEY, status],
    queryFn: () => integrationRequestsApi.internalList(status),
    retry: false,
  })

export const usePromoteIntegrationRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, categories }: { id: string; categories: string[] }) =>
      integrationRequestsApi.promote(id, categories),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INTERNAL_KEY }),
  })
}

export const useDeclineIntegrationRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => integrationRequestsApi.decline(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INTERNAL_KEY }),
  })
}

export const useIntegrationAnalytics = (
  params: { days?: number; granularity?: 'day' | 'week' | 'month' } = {},
) =>
  useQuery<IntegrationAnalytics>({
    queryKey: ['integration-analytics', params],
    queryFn: () => integrationRequestsApi.analytics(params),
    staleTime: 30000,
    retry: false,
  })

export const useIntegrationVoters = (id: string, enabled: boolean) =>
  useQuery<IntegrationVoter[]>({
    queryKey: ['integration-voters', id],
    queryFn: () => integrationRequestsApi.voters(id),
    enabled,
    retry: false,
  })

// --- Internal Roadmap tab ---
const ROADMAP_KEY = [...INTERNAL_KEY, 'roadmap']

export const useIntegrationRoadmap = () =>
  useQuery<RoadmapIntegration[]>({
    queryKey: ROADMAP_KEY,
    queryFn: () => integrationRequestsApi.roadmap(),
    retry: false,
  })

/**
 * Inline edit. Optimistic: the cell shows the new value immediately and rolls back
 * on error, so ranking a column of rows does not wait on a round trip per edit.
 */
export const useUpdateRoadmapIntegration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: RoadmapUpdate }) =>
      integrationRequestsApi.updateRoadmap(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: ROADMAP_KEY })
      const previous = queryClient.getQueryData<RoadmapIntegration[]>(ROADMAP_KEY)
      queryClient.setQueryData<RoadmapIntegration[]>(ROADMAP_KEY, (rows) =>
        rows?.map((r) => (r.id === id ? { ...r, ...patch } as RoadmapIntegration : r)),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(ROADMAP_KEY, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ROADMAP_KEY }),
  })
}
